import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageTransition from '../components/PageTransition';
import Countdown from '../components/Countdown';
import {
  COMMITTEES, WHY_JOIN, WHY_JOIN_SECTION,
  HOME_COMMITTEES, HOME_CTA, HERO_CONTENT,
  SITE_CONFIG, NAVBAR_CTA,
} from '../data';
import './HomePage.css';

export default function HomePage() {
  const { titleLine1, titleLine2Italic, titleLine2Rest } = HERO_CONTENT;

  return (
    <PageTransition>
      <div className="home-page">

        {/* ── Hero ── */}
        <section className="hero">
          <div className="hero-bg">
            <div className="hero-bg-grid" />
            <div className="hero-bg-orb hero-orb-1" />
            <div className="hero-bg-orb hero-orb-2" />
          </div>
          <div className="container hero-content">
            <motion.div
              className="hero-text"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
            >
              <span className="hero-edition">{HERO_CONTENT.editionBadge}</span>
              <h1 className="hero-title">
                {titleLine1}<br />
                <em>{titleLine2Italic}</em>{titleLine2Rest}
              </h1>
              <p className="hero-desc">{HERO_CONTENT.description}</p>
              <div className="hero-actions">
                <Link to={NAVBAR_CTA.path} className="btn-primary">
                  {HERO_CONTENT.primaryBtn}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15" height="15"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </Link>
                <Link to="/komiteler" className="btn-outline-white">
                  {HERO_CONTENT.secondaryBtn}
                </Link>
              </div>
              <div className="hero-stats">
                {HERO_CONTENT.stats.map((s, i) => (
                  <React.Fragment key={s.label}>
                    {i > 0 && <div className="stat-divider" />}
                    <div className="hero-stat">
                      <span className="stat-number">{s.number}</span>
                      <span className="stat-label">{s.label}</span>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="hero-right"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
            >
              <Countdown />
              <div className="hero-location">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                {SITE_CONFIG.location}
              </div>
            </motion.div>
          </div>
          <div className="hero-scroll">
            <span>{HERO_CONTENT.scrollLabel}</span>
            <div className="scroll-line" />
          </div>
        </section>

        {/* ── Why Join ── */}
        <section className="why-section">
          <div className="container">
            <div className="section-header">
              <span className="section-tag">{WHY_JOIN_SECTION.tag}</span>
              <h2 className="section-title">{WHY_JOIN_SECTION.title}</h2>
              <p className="section-desc">{WHY_JOIN_SECTION.description}</p>
            </div>
            <div className="why-grid">
              {WHY_JOIN.map((item, i) => (
                <motion.div
                  key={item.title}
                  className="why-card"
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.07, duration: 0.5 }}
                >
                  <span className="why-icon">{item.icon}</span>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Committees Preview ── */}
        <section className="committees-preview">
          <div className="container">
            <div className="section-header section-header-row">
              <div>
                <span className="section-tag">{HOME_COMMITTEES.tag}</span>
                <h2 className="section-title">{HOME_COMMITTEES.title}</h2>
              </div>
              <Link to="/komiteler" className="btn-secondary">
                {HOME_COMMITTEES.viewAllBtn}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </Link>
            </div>
            <div className="committees-preview-grid">
              {COMMITTEES.slice(0, 4).map((c, i) => (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Link to={`/komiteler/${c.slug}`} className="committee-preview-card">
                    <div className="committee-card-bar" style={{ background: c.color }} />
                    <span className="committee-card-icon">{c.icon}</span>
                    <h3>{c.title}</h3>
                    <p>{c.subtitle}</p>
                    <div className="committee-card-meta">
                      <span>{c.delegate_count} Delege</span>
                      <span className={`difficulty difficulty-${c.difficulty === 'Başlangıç' ? 'easy' : c.difficulty === 'Orta' ? 'medium' : 'hard'}`}>
                        {c.difficulty}
                      </span>
                    </div>
                    <span className="committee-card-arrow">→</span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA Banner ── */}
        <section className="cta-banner">
          <div className="container">
            <div className="cta-inner">
              <div className="cta-orb" />
              <div className="cta-content">
                <h2>{HOME_CTA.title}</h2>
                <p>{HOME_CTA.description}</p>
                <Link to={NAVBAR_CTA.path} className="btn-primary">
                  {HOME_CTA.btnLabel}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15" height="15"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </Link>
              </div>
            </div>
          </div>
        </section>

      </div>
    </PageTransition>
  );
}
